import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  formatLoaderSerial,
  LOADER_INSPECTION_LABELS,
  LOADER_REMOVAL_REASON_LABELS,
  type LoaderInstallDto,
  type LoaderUnitRevDto,
} from '@bhops/shared';
import { loadersApi } from '@/api/loaders';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { LoaderConfigCode } from '@/components/LoaderConfigCode';
import { LoaderStatusPill } from '@/components/StatusPill';
import { kstYmd } from '@/lib/format-date';
import { canEditMfg } from '@/permissions';
import { useAuthStore } from '@/stores/auth';
import { EditLoaderModal } from './manufacturing/EditLoaderModal';

// docs/plan/loader-history-traceability-2026-07 — 로더(로봇 팔) 상세.
//   상단: 시리얼 + 디코드 배지 + 상태/업체/입고 정보 (편집).
//   중단: 수리 계보(전/후 로더 링크).
//   하단: 거쳐 간 로봇(장착 이력 표) + 적용된 개조 목록.
// 개조 적용/해제는 '개조 관리' 페이지에서 진행 — 여기선 읽기 전용.
//
// 개선 9건 차수1(26-08-04) — 삭제된 로더도 URL 로는 그대로 열린다. 아무 표시가
// 없으면 살아 있는 로더로 착각하므로 '삭제됨' 안내를 단다. 삭제·복구·완전삭제
// 버튼은 목록(LoadersListPage)에만 둔다 — 조작 자리를 한 곳으로 모은다.

export function LoaderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role ? canEditMfg(role) : false;
  const [editOpen, setEditOpen] = useState(false);

  const q = useQuery({
    queryKey: ['loader', id],
    queryFn: () => loadersApi.get(id!),
    enabled: !!id,
  });

  if (q.isLoading) return <div style={loadingStyle}>불러오는 중…</div>;
  if (q.error) return <div style={errorStyle}>{extractApiError(q.error)}</div>;
  const loader = q.data;
  if (!loader) return <div style={emptyBlock}>로더를 찾을 수 없습니다.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/manufacturing/loaders" style={backLink}>
        <Icon name="arrow-left" size={14} aria-hidden /> 로더 목록
      </Link>

      {/* 개선 9건 차수1(26-08-04) — 삭제된 로더 안내. */}
      {loader.deletedAt && (
        <div style={deletedBanner} data-testid="loader-deleted-banner">
          {/* 한국시간 통일 5차(26-08-14) — 삭제 시각은 서버가 `new Date()` 로 찍는
           **진짜 순간**이라 UTC 로 자르면 한국 오전 0~9시 삭제분이 어제로 보였다. */}
          {kstYmd(loader.deletedAt) ?? '–'}에 삭제된 로더입니다. 로더 목록에서 「삭제 포함」을 켜면
          보이고, 관리자가 복구할 수 있습니다.
        </div>
      )}

      {/* 헤더 카드 */}
      <section style={card}>
        <div style={headerRow}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={serialText}>{loader.serial}</span>
            <span style={decodeChip}>{formatLoaderSerial(loader.serial)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <LoaderStatusPill status={loader.status} />
            {canEdit && (
              <Button type="button" variant="secondary" onClick={() => setEditOpen(true)}>
                편집
              </Button>
            )}
          </div>
        </div>

        <div style={kvGrid}>
          <KV label="업체" value={loader.vendor || '—'} />
          {/* 입고일은 date-only(`${ymd}T00:00:00.000Z`)라 UTC/KST 어느 쪽으로 읽어도
              같은 날짜지만, 한 카드 안에서 날짜 읽는 규칙이 둘이면 다음 사람이 어느
              쪽을 복사할지 모른다 — 표시는 전부 KST 정본 한 벌로 맞춘다. */}
          <KV label="입고일" value={kstYmd(loader.receivedAt) ?? '–'} />
          <KV label="입고검사" value={LOADER_INSPECTION_LABELS[loader.inspectionResult]} />
          {/* 구성코드 — 올리면 코드별로 어떤 개조인지. 이 카드는 화면에 하나뿐이라
              포커스로도 뜨게 둔다(목록과 달리 탭 정지점이 늘어나지 않는다). */}
          <KV
            label="구성코드"
            value={<LoaderConfigCode code={loader.configCode} focusable fallback="—" />}
          />
        </div>

        {loader.notes && <div style={notesBox}>{loader.notes}</div>}
      </section>

      {/* 수리 계보 */}
      {(loader.repairedFromSerial || loader.repairs.length > 0) && (
        <section style={lineageCard}>
          {loader.repairedFromSerial && (
            <div style={lineageRow}>
              수리 전 로더:{' '}
              <Link
                to={`/manufacturing/loaders/${loader.repairedFromLoaderUnitId}`}
                style={lineageLink}
              >
                {loader.repairedFromSerial}
              </Link>
            </div>
          )}
          {loader.repairs.length > 0 && (
            <div style={lineageRow}>
              수리 후 로더:{' '}
              {loader.repairs.map((r, i) => (
                <span key={r.id}>
                  {i > 0 && ', '}
                  <Link to={`/manufacturing/loaders/${r.id}`} style={lineageLink}>
                    {r.serial}
                  </Link>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 거쳐 간 로봇 */}
      <section>
        <h3 style={sectionHeaderStyle}>
          거쳐 간 로봇{' '}
          <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>
            ({loader.installs.length})
          </span>
        </h3>
        {loader.installs.length === 0 ? (
          <div style={emptyBlock}>장착 이력이 없습니다.</div>
        ) : (
          <div style={tableWrap}>
            <table style={tableBase}>
              <thead>
                <tr>
                  <th style={th}>로봇</th>
                  <th style={th}>방향</th>
                  <th style={th}>장착일</th>
                  <th style={th}>탈거일</th>
                  <th style={th}>사유</th>
                </tr>
              </thead>
              <tbody>
                {loader.installs.map((i: LoaderInstallDto) => (
                  <tr key={i.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      <Link to={`/robots/${i.robotId}`} style={lineageLink}>
                        #{i.robotSerialShort ?? i.robotSerialLong ?? i.robotId.slice(0, 8)}
                      </Link>
                    </td>
                    <td style={td}>
                      {i.direction ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}
                    </td>
                    {/* 장착/탈거 시각은 화면이 날짜를 안 보내면 서버가 `new Date()` 로
                        찍는다(loaders.service.ts `dto.installedAt ? … : new Date()`) —
                        즉 **진짜 순간**이 섞여 있어 KST 로 읽어야 한다. */}
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {kstYmd(i.installedAt) ?? '–'}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {i.removedAt ? (
                        (kstYmd(i.removedAt) ?? '–')
                      ) : (
                        <span style={{ color: 'var(--fg-muted)' }}>장착 중</span>
                      )}
                    </td>
                    <td style={td}>
                      {i.removalReason ? (
                        LOADER_REMOVAL_REASON_LABELS[i.removalReason]
                      ) : (
                        <span style={{ color: 'var(--fg-muted)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 적용 개조 */}
      <section>
        <h3 style={sectionHeaderStyle}>
          적용 개조{' '}
          <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>({loader.revs.length})</span>
        </h3>
        {loader.revs.length === 0 ? (
          <div style={emptyBlock}>적용된 개조가 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loader.revs.map((rev: LoaderUnitRevDto) => (
              <div key={rev.id} style={revRow}>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{rev.code}</span>
                <span>{rev.displayName}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {/* 개조 적용일 — 편집 모달로 개조를 켜면 서버가 그 순간을 찍는다
                      (loaders.service.ts setLoaderRevs: `const appliedAt = new Date()`).
                      한국 오전 0~9시에 적용하면 UTC 자르기는 **어제**를 찍었다. */}
                  {kstYmd(rev.appliedAt) ?? '–'}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {canEdit && editOpen && (
        <EditLoaderModal loaderId={loader.id} onClose={() => setEditOpen(false)} />
      )}
    </div>
  );
}

// mono 옵션은 구성코드 칸 하나만 쓰던 것인데, 그 칸이 스스로 글꼴을 아는 부품
// (LoaderConfigCode)으로 바뀌면서 쓰는 곳이 없어졌다 — 남겨 두면 다음 사람이 죽은
// 선택지를 붙잡는다.
function KV({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{label}</span>
      <span style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

const backLink: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-muted)',
  textDecoration: 'none',
  width: 'fit-content',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};
const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};
const serialText: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--fg-default)',
  wordBreak: 'break-all',
};
const decodeChip: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-pill)',
  padding: '2px 10px',
  width: 'fit-content',
};
const kvGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 12,
};
const notesBox: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-default)',
  background: 'var(--bg-sunken)',
  borderRadius: 'var(--radius-sm)',
  padding: 10,
  whiteSpace: 'pre-wrap',
};
const lineageCard: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-sunken)',
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
};
const lineageRow: CSSProperties = { color: 'var(--fg-muted)' };
const lineageLink: CSSProperties = {
  color: 'var(--fg-brand)',
  fontWeight: 600,
  textDecoration: 'none',
};
const sectionHeaderStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  margin: '0 0 8px',
  color: 'var(--fg-default)',
};
const tableWrap: CSSProperties = {
  overflowX: 'auto',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
const tableBase: CSSProperties = { width: '100%', borderCollapse: 'collapse' };
const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};
const td: CSSProperties = { fontSize: 13, padding: '8px 12px', verticalAlign: 'middle' };
const revRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
const loadingStyle: CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--fg-muted)' };
const errorStyle: CSSProperties = {
  padding: 16,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
  borderRadius: 'var(--r-card)',
};
const emptyBlock: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
};
// 개선 9건 차수1(26-08-04) — 삭제된 로더 안내 배너.
const deletedBanner: CSSProperties = {
  fontSize: 13,
  padding: '10px 12px',
  borderRadius: 'var(--r-card)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-sunken)',
  color: 'var(--fg-muted)',
};
